<template>

  <UForm :schema="schema" :state="state" @submit="onSubmit"
    class="font-medium text-sm text-slate-950 flex flex-col content-start max-w-[500px] gap-4">
    <FormSection title="Canale de notificare">
      <div class="space-y-3">
        <UAlert v-if="saved" title="Setările au fost salvate cu succes!" color="primary" variant="subtle"
          icon="i-lucide-info" />
        <!-- WEB -->
        <UFormField name="ch_web">
          <UCheckbox v-model="state.ch_web" label="Activează notificările pe pagina Web" />
        </UFormField>

        <!-- EMAIL -->
        <UFormField name="ch_email">
          <UCheckbox v-model="state.ch_email" label="Activează notificările prin Email" />

          <div v-if="state.ch_email" class="mt-2">
            <UFormField name="email" label="Adresă email" required>
              <UInput v-model="state.email" class="block" size="lg" />
            </UFormField>
          </div>
        </UFormField>

        <!-- SMS -->
        <UFormField name="ch_sms">
          <UCheckbox v-model="state.ch_sms" label="Activează notificările prin SMS" />

          <div v-if="state.ch_sms" class="mt-2">
            <UFormField name="sms_no" label="Număr telefon" required>
              <UInput v-model="state.sms_no" class="block" size="lg" />
            </UFormField>
          </div>
        </UFormField>

        <UFormField name="submit">
        <div class="flex flex-col md:flex-row gap-2">
          <UButton label="Salvează" type="submit" icon="i-lucide-save" size="xl" loading-auto />
          <UButton v-if="!welcome" label="Export notificari" @click="downloadExcel" color="neutral" size="xl" variant="outline" />
        </div>
        </UFormField>

      </div>

    </FormSection>
  </UForm>
</template>

<script setup lang="ts">
import { z } from "zod"
import { type FormSubmitEvent } from "#ui/types"

const loading = ref(false)
const saved = ref(false)
const account = useAccount().account
const notifications = useNotification().notifications;

const props = defineProps({
  welcome: {
    type: Boolean,
    default: false
  }
})

import * as XLSX from "xlsx";

function exportToExcel<T extends Record<string, any>>(
  jsonData: T[],
  fileName = "export.xlsx",
  includeKeys?: (keyof T)[],
  headers?: Partial<Record<keyof T, string>> // 4th argument: header mapping
) {
  if (!jsonData || !jsonData.length) return;

  const dataToExport = includeKeys
    ? jsonData.map((item) => {
        const filtered: Record<string, any> = {};
        includeKeys.forEach((key) => {
          const headerName = headers?.[key] ?? (key as string); // use mapped header if exists
          filtered[headerName] = item[key];
        });
        return filtered;
      })
    : jsonData;

  const worksheet = XLSX.utils.json_to_sheet(dataToExport);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Listă de notificări");

  XLSX.writeFile(workbook, fileName);
}

const keysToInclude: (keyof BackendNotification)[] = [
  "subject",
  "body",
  "link",
  "date_created",
  "denumireStatus",
  "priorityStatus",
];

const headersMap: Partial<Record<keyof BackendNotification, string>> = {
  subject: "Subiect",
  body: "Conținut",
  link: "Link",
  date_created: "Data notificării",
  denumireStatus: "Status",
  priorityStatus: "Prioritate",
};

function downloadExcel() {
  exportToExcel(notifications.value, "export_notificari.xlsx", keysToInclude, headersMap);
}

const schema = z.object({
  ch_sms: z.boolean(),
  ch_email: z.boolean(),
  ch_web: z.boolean(),

  email: z.string().email("Email invalid").optional().or(z.literal("")),
  sms_no: z.string().optional().or(z.literal("")),

  submit: z.string().optional()
})
  .refine((data) => !data.ch_email || !!data.email, {
    message: "Emailul este obligatoriu atunci când căsuța este bifată!",
    path: ["email"]
  })
  .refine((data) => !data.ch_sms || !!data.sms_no, {
    message: "Numărul de telefon este obligatoriu atunci când căsuța este bifată!",
    path: ["sms_no"]
  })

type Schema = z.output<typeof schema>

const state = reactive({
  key: account.value?.key,
  ch_web: false,
  ch_email: false,
  ch_sms: false,

  email: "",
  sms_no: "",

  terms_conditions: false,
  gdpr: false,
  propria_raspundere: false
})

onMounted(() => {
  if (!account.value) return
  
  state.ch_web = !!account.value.ch_web
  state.ch_email = !!account.value.ch_email
  state.ch_sms = !!account.value.ch_sms
  state.terms_conditions = !!account.value.terms_conditions
  state.propria_raspundere = !!account.value.propria_raspundere
  state.gdpr = !!account.value.gdpr
  state.email = account.value.email ?? ""
  state.sms_no = account.value.sms_no ?? ""
})

async function onSubmit(event: FormSubmitEvent<Schema>) {
  loading.value = true
  saved.value = false

  useAccount().updateAccount(state)

  loading.value = false
  saved.value = true
}
</script>